import { useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateValidationError } from "@mui/x-date-pickers/models/validation";
import { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormClearErrors,
  UseFormSetError,
} from "react-hook-form";

import { dateFromString, dateToDateOnlyFormat } from "../../helpers/dates";
import { setControllerFieldRef } from "../../helpers/form";
import { getNestedProperty } from "../../helpers/object";
import {
  ValidationOptions,
  validationOptionsToControlRules,
} from "../../types/Validations";

interface APDateFieldProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  defaultValue: PathValue<T, Path<T>> | undefined;
  errors: FieldErrors<T>;
  setError: UseFormSetError<T>;
  clearErrors: UseFormClearErrors<T>;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  minDateErrorMessage?: string;
  maxDateErrorMessage?: string;
  showRequired?: boolean;
  highlightOnEmpty?: boolean;
  readOnly?: boolean;
  clearable?: boolean;
  validations?: ValidationOptions;
  hidden?: boolean;
  dateOnlyFormat?: boolean;
}

const APDateField = <T extends FieldValues>({
  name,
  label,
  control,
  defaultValue,
  errors,
  setError,
  clearErrors,
  minDate,
  maxDate,
  minDateErrorMessage,
  maxDateErrorMessage,
  showRequired,
  highlightOnEmpty,
  readOnly,
  clearable,
  validations,
  hidden,
  dateOnlyFormat = false,
}: APDateFieldProps<T>) => {
  const [dateError, setDateError] = useState<
    DateValidationError | "required" | null
  >(null);
  const lastValue = useRef<Dayjs | null>(null);

  const formError = getNestedProperty(name, errors);
  const theme = useTheme();

  const getErrorMessage = (error: DateValidationError | "required") => {
    let message = "Invalid date";

    switch (error) {
      case "minDate":
        if (minDateErrorMessage) {
          message = minDateErrorMessage;
        }
        break;
      case "maxDate":
        if (maxDateErrorMessage) {
          message = maxDateErrorMessage;
        }
        break;
      case "required":
        message = "This field is required";
        break;
      default:
        break;
    }

    return message;
  };

  useEffect(() => {
    if (dateError) {
      setError(name, {
        type: "string",
        message: getErrorMessage(dateError) ?? "",
      });
    } else {
      clearErrors(name);
    }
  }, [dateError]);

  return (
    <Controller<T>
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={{
        validate: () => !dateError,
        ...(validations
          ? validationOptionsToControlRules(label, validations)
          : {}),
      }}
      render={({ field: { ref, ...field } }) => (
        <DatePicker
          {...field}
          value={field.value ? dateFromString(field.value) : null}
          format="DD/MM/YYYY"
          label={showRequired ? `${label} *` : label}
          minDate={minDate}
          maxDate={maxDate}
          onError={(newError) => setDateError(newError)}
          readOnly={readOnly}
          sx={{
            display: hidden ? "none" : undefined,
          }}
          onChange={(date) => {
            lastValue.current = date;

            if (date && dateError === "required") {
              setDateError(null);
            }

            field.onChange(
              dateOnlyFormat && date ? dateToDateOnlyFormat(date) : date
            );
          }}
          onClose={() => {
            if (!lastValue.current && validations?.required) {
              setDateError("required");
            }
          }}
          slotProps={{
            actionBar: {
              actions: ["clear", "accept"],
            },
            textField: {
              inputRef: (el: HTMLInputElement | null) =>
                setControllerFieldRef(ref, el),
              helperText: `${formError ? formError?.message : ""}`,
              error: !!formError || (highlightOnEmpty && !field.value),
              required: validations?.required ?? false,
              sx: {
                "& .MuiInputAdornment-root": {
                  marginRight: 1, // Add margin to the icon container
                },
                display: hidden ? "none" : undefined,
              },
            },
            field: {
              clearable,
            },
            day: {
              sx: {
                "&.Mui-selected": {
                  color: `${theme.palette.onPrimary.main} !important`,
                  backgroundColor: `${theme.palette.primary.main} !important`,
                },
                "&.Mui-selected:hover": {
                  color: "onPrimary.main",
                  backgroundColor: "primary.main",
                },
              },
            },
          }}
        />
      )}
    />
  );
};

export default APDateField;
